import { KEY_FIREFOX_ENTER, KEY_RETURN } from "keycode-js";
import type { PropsWithChildren } from "react";
import { useEffect, useRef } from "react";

// A subset of getModifierState's arg
// https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/getModifierState
export type ModifierStateType = "Alt" | "AltGraph" | "Control" | "Meta" | "Shift";
export const AllKeyModifiers: ReadonlyArray<ModifierStateType> = ["Alt", "AltGraph", "Control", "Meta", "Shift"];
export const DefaultKeyModifiers: ReadonlyArray<ModifierStateType> = AllKeyModifiers.filter(
  (modifier) => modifier !== "Shift",
);

export interface HotKeyDefinition<CommandType> {
  keyCode: number; // TODO: be type-safe

  // By default (i.e. `modifiers: true`), <HotKey/> does not look at Shift key as a modifier.
  // Use `AllKeyModifiers` if you need Shift key.
  modifier?: boolean | ReadonlyArray<ModifierStateType>;
  command: CommandType;

  preventDefault?: boolean; // default to true
  stopPropagation?: boolean; // default to true
  disabledIf?(event: KeyboardEvent): boolean;
}

interface Props<CommandType> {
  definitions: ReadonlyArray<HotKeyDefinition<CommandType>>;

  onDispatch(command: CommandType, event: KeyboardEvent);
}

function checkModifierState({ modifier }: HotKeyDefinition<any>, event: KeyboardEvent): boolean {
  if (modifier == null) {
    return true; // modifiers do not matter
  } else if (typeof modifier === "boolean") {
    // If modifier = true, the event must be modified. Otherwise, must not.
    return DefaultKeyModifiers.some((m) => event.getModifierState(m)) === modifier;
  } else {
    return modifier.some((m) => event.getModifierState(m));
  }
}

function normalizeKeyCode(keyCode: number) {
  return keyCode === KEY_FIREFOX_ENTER ? KEY_RETURN : keyCode;
}

function handleKeyDown<C>(props: Props<C>, event: KeyboardEvent) {
  const keyCode = normalizeKeyCode(event.keyCode);

  const { definitions } = props;

  // TODO: use Map to find it with O(1) algorithm
  const definition = definitions.find((d) => {
    if (d.keyCode === keyCode) {
      return checkModifierState(d, event);
    }
    return false;
  });

  if (!definition) {
    return;
  }

  if (definition.disabledIf && definition.disabledIf(event)) {
    return;
  }

  if (definition.preventDefault !== false) {
    event.preventDefault();
  }

  if (definition.stopPropagation !== false) {
    event.stopPropagation();
  }

  props.onDispatch(definition.command, event);
}

export function useHotKey<C>({ definitions, onDispatch }: Props<C>) {
  useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      handleKeyDown({ definitions, onDispatch }, event);
    };
    document.addEventListener("keydown", handler);

    return () => {
      document.removeEventListener("keydown", handler);
    };
  }, [onDispatch, definitions]);
}

/**
 * Function component `<HotKey/>` to bind hot-keys along with React lifecycle.
 */
export function HotKey<C>(props: Props<C>) {
  useHotKey(props);
  return null;
}

// Enable hot keys in the component
export function HotKeyWrapper<C>({ definitions, onDispatch, children }: PropsWithChildren<Props<C>>) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const dom = ref.current;
    if (!dom) {
      return;
    }

    const handler = (event: KeyboardEvent): void => {
      handleKeyDown({ definitions, onDispatch }, event);
    };
    dom.addEventListener("keydown", handler);

    return () => {
      dom.removeEventListener("keydown", handler);
    };
  }, [ref, definitions, onDispatch]);

  return <div ref={ref}>{children}</div>;
}
